/**
 * Eisenhower Matrix Renderer
 * 
 * Renders 2x2 quadrant grid (Importance vs Urgency).
 * Positions items based on coordinates and color-codes quadrants.
 */

import type { VisualizationRenderer } from '../registry.js';
import { designTokens, escapeHtml } from '../../../core/design-system.js';

export const renderEisenhowerMatrix: VisualizationRenderer = (data, meta) => {
  // Expected data format: array of { label: string, importance: number, urgency: number }
  // Or: { task: string, urgency: number, importance: number }
  if (!Array.isArray(data) || data.length === 0) {
    return renderEmptyState('No data to display');
  }

  // Normalize data format
  const items = data.map(item => {
    const label = item.label || item.task || item.name || 'Untitled';
    const importance = item.importance !== undefined ? Number(item.importance) : 0;
    const urgency = item.urgency !== undefined ? Number(item.urgency) : 0;
    
    return {
      label: String(label),
      importance: Math.max(0, Math.min(1, importance)), // Clamp to 0-1
      urgency: Math.max(0, Math.min(1, urgency)) // Clamp to 0-1
    };
  });

  // Matrix dimensions
  const size = 500;
  const padding = 20;
  const quadrantSize = (size - padding * 2) / 2;
  const centerX = padding + quadrantSize;
  const centerY = padding + quadrantSize;

  // Quadrant colors
  const quadrantColors = {
    'do-now': 'rgba(255, 100, 100, 0.2)',      // High urgency, high importance
    'schedule': 'rgba(100, 200, 255, 0.2)',    // Low urgency, high importance
    'delegate': 'rgba(255, 200, 100, 0.2)',   // High urgency, low importance
    'ignore': 'rgba(150, 150, 150, 0.1)'       // Low urgency, low importance
  };

  const quadrantBorders = {
    'do-now': 'rgba(255, 100, 100, 0.4)',
    'schedule': 'rgba(100, 200, 255, 0.4)',
    'delegate': 'rgba(255, 200, 100, 0.4)',
    'ignore': 'rgba(150, 150, 150, 0.2)'
  };

  // Generate quadrant backgrounds
  const quadrants = [
    { id: 'do-now', x: centerX, y: padding, label: 'Do Now', description: 'Urgent & Important' },
    { id: 'schedule', x: padding, y: padding, label: 'Schedule', description: 'Not Urgent & Important' },
    { id: 'delegate', x: centerX, y: centerY, label: 'Delegate', description: 'Urgent & Not Important' },
    { id: 'ignore', x: padding, y: centerY, label: 'Ignore', description: 'Not Urgent & Not Important' }
  ];

  const quadrantElements = quadrants.map(quad => {
    const isTop = quad.id === 'do-now' || quad.id === 'schedule';
    const isLeft = quad.id === 'schedule' || quad.id === 'ignore';
    
    return `
      <g>
        <rect
          x="${quad.x}"
          y="${quad.y}"
          width="${quadrantSize}"
          height="${quadrantSize}"
          fill="${quadrantColors[quad.id as keyof typeof quadrantColors]}"
          stroke="${quadrantBorders[quad.id as keyof typeof quadrantBorders]}"
          stroke-width="1"
        />
        <text
          x="${quad.x + quadrantSize / 2}"
          y="${quad.y + 20}"
          text-anchor="middle"
          font-size="${designTokens.fontSize.sm}"
          font-weight="600"
          fill="${designTokens.contentColor}"
        >${escapeHtml(quad.label)}</text>
        <text
          x="${quad.x + quadrantSize / 2}"
          y="${quad.y + 38}"
          text-anchor="middle"
          font-size="${designTokens.fontSize.xs}"
          fill="${designTokens.contentColorMuted}"
        >${escapeHtml(quad.description)}</text>
      </g>
    `;
  }).join('');

  // Generate axis labels
  const axisLabels = `
    <text
      x="${size / 2}"
      y="${size - 5}"
      text-anchor="middle"
      font-size="${designTokens.fontSize.sm}"
      font-weight="600"
      fill="${designTokens.contentColor}"
    >Urgency →</text>
    <text
      x="10"
      y="${size / 2}"
      text-anchor="middle"
      font-size="${designTokens.fontSize.sm}"
      font-weight="600"
      fill="${designTokens.contentColor}"
      transform="rotate(-90 10 ${size / 2})"
    >Importance →</text>
  `;

  // Generate axis lines
  const axisLines = `
    <line
      x1="${centerX}"
      y1="${padding}"
      x2="${centerX}"
      y2="${size - padding}"
      stroke="${designTokens.contentColor}"
      stroke-width="2"
      opacity="0.5"
    />
    <line
      x1="${padding}"
      y1="${centerY}"
      x2="${size - padding}"
      y2="${centerY}"
      stroke="${designTokens.contentColor}"
      stroke-width="2"
      opacity="0.5"
    />
  `;

  // Position items on matrix
  const itemElements = items.map((item, index) => {
    // Map importance (0-1) to Y position (bottom to top)
    const y = size - padding - (item.importance * quadrantSize * 2);
    // Map urgency (0-1) to X position (left to right)
    const x = padding + (item.urgency * quadrantSize * 2);
    
    // Determine quadrant for color
    const isHighUrgency = item.urgency >= 0.5;
    const isHighImportance = item.importance >= 0.5;
    let quadrantColor = 'rgba(255, 255, 255, 0.8)';
    if (isHighUrgency && isHighImportance) quadrantColor = 'rgba(255, 100, 100, 0.9)';
    else if (!isHighUrgency && isHighImportance) quadrantColor = 'rgba(100, 200, 255, 0.9)';
    else if (isHighUrgency && !isHighImportance) quadrantColor = 'rgba(255, 200, 100, 0.9)';
    else quadrantColor = 'rgba(150, 150, 150, 0.7)';

    const tooltipId = `eisenhower-tooltip-${Date.now()}-${index}`;
    
    return `
      <g>
        <circle
          cx="${x}"
          cy="${y}"
          r="8"
          fill="${quadrantColor}"
          stroke="${designTokens.contentColor}"
          stroke-width="1.5"
          style="cursor: pointer;"
          data-label="${escapeHtml(item.label)}"
          data-importance="${item.importance.toFixed(2)}"
          data-urgency="${item.urgency.toFixed(2)}"
          onmouseenter="
            const tooltip = document.getElementById('${tooltipId}');
            if (tooltip) tooltip.style.display = 'block';
          "
          onmouseleave="
            const tooltip = document.getElementById('${tooltipId}');
            if (tooltip) tooltip.style.display = 'none';
          "
        />
        <g id="${tooltipId}" style="display: none; pointer-events: none;">
          <rect
            x="${x + 12}"
            y="${y - 30}"
            width="150"
            height="50"
            fill="rgba(30, 32, 40, 0.95)"
            stroke="${designTokens.borderColor}"
            stroke-width="1"
            rx="4"
          />
          <text
            x="${x + 17}"
            y="${y - 12}"
            font-size="${designTokens.fontSize.xs}"
            font-weight="600"
            fill="${designTokens.contentColor}"
          >${escapeHtml(item.label)}</text>
          <text
            x="${x + 17}"
            y="${y + 2}"
            font-size="${designTokens.fontSize.xs}"
            fill="${designTokens.contentColorMuted}"
          >Urgency: ${(item.urgency * 100).toFixed(0)}%</text>
          <text
            x="${x + 17}"
            y="${y + 16}"
            font-size="${designTokens.fontSize.xs}"
            fill="${designTokens.contentColorMuted}"
          >Importance: ${(item.importance * 100).toFixed(0)}%</text>
        </g>
      </g>
    `;
  }).join('');

  return `
    <div style="width: 100%;">
      <div style="
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
      ">
        <svg
          width="${size}"
          height="${size}"
          style="
            display: block;
            max-width: 100%;
            height: auto;
          "
        >
          ${quadrantElements}
          ${axisLines}
          ${axisLabels}
          ${itemElements}
        </svg>
      </div>
    </div>
  `;
};

function renderEmptyState(message: string): string {
  return `
    <div style="
      padding: ${designTokens.padding.xl};
      text-align: center;
      color: ${designTokens.contentColorMuted};
      font-size: ${designTokens.fontSize.sm};
    ">${escapeHtml(message)}</div>
  `;
}


